import { PartFinderReport } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { useClearPartFinderReportDescription } from '@/hooks/api/part-finder-reports/useClearPartFinderReportDescription';
import { useUpdatePartFinderReportDescription } from '@/hooks/api/part-finder-reports/useUpdatePartFinderReportDescription';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { getApiError } from '@/types/apiError';
import { CheckOutlined, CloseOutlined, EditOutlined } from '@mui/icons-material';
import { Alert, Box, CircularProgress, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';

const MAX_DESCRIPTION_LENGTH = 1000;

const HoverRow = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingRight: '32px',
  '& .edit-affordance': {
    opacity: 0,
    transition: theme.transitions.create('opacity', { duration: theme.transitions.duration.shortest }),
  },
  '&:hover .edit-affordance, &:focus-within .edit-affordance': { opacity: 1 },
}));

const EditAffordance = styled(Box)({ position: 'absolute', top: '-6px', right: '-4px' });

const InlineActions = styled(InputAdornment)({
  alignSelf: 'flex-end',
  marginBottom: '6px',
  marginRight: '4px',
  gap: '2px',
});

export const EditableDescription = ({ report }: { report: PartFinderReport }) => {
  const savedValue = report.description ?? '';

  const { permissions } = useGetCurrentUser();
  const canEdit = permissions?.canEditPartFinderReport ?? false;

  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(savedValue);
  const updateMutation = useUpdatePartFinderReportDescription(report.id);
  const clearMutation = useClearPartFinderReportDescription(report.id);

  const isPending = updateMutation.isPending || clearMutation.isPending;
  const activeError = updateMutation.error ?? clearMutation.error;

  useEffect(() => {
    if (isEditing) return;
    setValue(savedValue);
  }, [savedValue, isEditing]);

  if (!canEdit) {
    return <BodyText color={savedValue ? 'text.primary' : 'text.secondary'}>{savedValue || 'None'}</BodyText>;
  }

  const trimmed = value.trim();
  const isDirty = trimmed !== savedValue.trim();
  const apiError = activeError ? getApiError(activeError, 'Failed to save description') : undefined;

  const resetMutations = () => {
    updateMutation.reset();
    clearMutation.reset();
  };

  const handleSave = () => {
    if (!isDirty) return;
    resetMutations();
    const onSuccess = () => {
      setValue(trimmed);
      setIsEditing(false);
    };
    if (trimmed === '') {
      clearMutation.mutate(undefined, { onSuccess });
    } else {
      updateMutation.mutate({ description: trimmed }, { onSuccess });
    }
  };

  const handleCancelEdit = () => {
    resetMutations();
    setValue(savedValue);
    setIsEditing(false);
  };

  const handleStartEdit = () => {
    resetMutations();
    setIsEditing(true);
  };

  if (isEditing) {
    return (
      <Box>
        <TextField
          value={value}
          onChange={event => setValue(event.target.value)}
          placeholder='Describe the part'
          multiline
          minRows={2}
          maxRows={8}
          fullWidth
          size='small'
          disabled={isPending}
          autoFocus
          inputProps={{ 'aria-label': 'Description', maxLength: MAX_DESCRIPTION_LENGTH }}
          slotProps={{
            input: {
              endAdornment: (
                <InlineActions position='end'>
                  <Tooltip title='Cancel edit'>
                    <span>
                      <IconButton size='small' onClick={handleCancelEdit} disabled={isPending} aria-label='Cancel edit'>
                        <CloseOutlined fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {(isDirty || isPending) && (
                    <Tooltip title='Save description'>
                      <span>
                        <IconButton
                          size='small'
                          color='primary'
                          onClick={handleSave}
                          disabled={isPending}
                          aria-label='Save description'
                        >
                          {isPending ? <CircularProgress size={16} /> : <CheckOutlined fontSize='small' />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </InlineActions>
              ),
            },
          }}
        />
        {apiError && (
          <Alert severity='error' sx={{ marginTop: '8px' }}>
            {apiError.message}
          </Alert>
        )}
      </Box>
    );
  }

  return (
    <HoverRow>
      <BodyText color={savedValue ? 'text.primary' : 'text.secondary'} sx={{ whiteSpace: 'pre-wrap' }}>
        {savedValue || 'None'}
      </BodyText>
      <EditAffordance className='edit-affordance'>
        <Tooltip title='Edit description'>
          <IconButton size='small' onClick={handleStartEdit} aria-label='Edit description'>
            <EditOutlined fontSize='small' />
          </IconButton>
        </Tooltip>
      </EditAffordance>
    </HoverRow>
  );
};
